import React, { useRef, useState } from 'react';
import { CrossIcon, DownArrowIcon, PlusIcon } from 'assets/icons';
import { Popover } from '@headlessui/react';
import AmountFilterPopoverPanel from 'components/custom-amount-filter/AmountFilterPopoverPanel';
import DropdownPopoverPanel from 'components/dropdown-with-search/DropdownPopoverPanel';
import CustomAmountFilter, { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';
import LocationFilterPopover, { LocationFilter } from 'components/location-filter/LocationFilterPopover';
import CustomDateRangePicker, {
  CustomDateRangePickerProps
} from 'components/react-date-range-picker/ReactDateRangePicker';
import If from 'components/If/If';

export enum FilterDropdownType {
  DROPDOWN = 'dropdown',
  AMOUNT = 'amount',
  DATE = 'date',
  TEXT = 'text',
  LOCATION = 'location'
}

export interface DateFilter {
  startDate: string | null;
  endDate: string | null;
}

export interface FilterOption {
  id: string;
  label: string;
  type: FilterDropdownType;
  idKey?: string;
  nameKey?: string;
  secondaryDataKey?: string;
  showSearchInFilter?: boolean;
  hasNoOption?: boolean;
  isAmountFilter?: boolean;
  dateFilterProps?: Partial<CustomDateRangePickerProps>;
}

export interface AppliedFilter extends FilterOption {
  value: string[] | AmountFilter | LocationFilter | DateFilter;
  displayValue: string;
}

interface MoreFiltersProps {
  onFilterApply: (filters: {
    selectedFilterItems: string[] | AmountFilter | LocationFilter | DateFilter;
    filter: FilterOption;
  }) => void;
  onFilterRemove: (filterId: string) => void;
  appliedFilters: AppliedFilter[];
  filterOptions: FilterOption[];
  className?: string;
  getFilterData?: (filterId: string) => Array<Record<string, any>>;
  buttonClassName?: string;
}

const MoreFilters: React.FC<MoreFiltersProps> = ({
  onFilterApply,
  onFilterRemove,
  appliedFilters = [],
  filterOptions,
  getFilterData,
  buttonClassName = ''
}) => {
  const [selectedFilter, setSelectedFilter] = useState<FilterOption | null>(null);
  const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
  const moreFiltersButtonRef = useRef<HTMLButtonElement>(null);
  const appliedFilterButtonRef = useRef<HTMLButtonElement>(null);
  const [amountOperation, setAmountOperation] = useState<AmountFilterOperation>(AmountFilterOperation.ALL);
  const [startValue, setStartValue] = useState<number | null>(null);
  const [endValue, setEndValue] = useState<number | null>(null);
  const [amountFilter, setAmountFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });

  const [selectedDraftItems, setSelectedDraftItems] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    startDate: null,
    endDate: null
  });
  const [datePickerPosition, setDatePickerPosition] = useState<'left' | 'right'>('left');

  // Function to calculate optimal position for date picker
  const calculateDatePickerPosition = (buttonElement: HTMLElement | null): 'left' | 'right' => {
    if (!buttonElement) return 'left';

    const rect = buttonElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const datePickerWidth = 524; // Approximate width of date picker

    const spaceOnRight = viewportWidth - rect.left;
    const spaceOnLeft = rect.right;
    // If not enough space on right but enough on left, position to the right
    if (spaceOnRight < datePickerWidth && spaceOnLeft >= datePickerWidth) {
      return 'right';
    }
    return 'left';
  };

  const getFilterOptions = (filterId: string) => {
    if (getFilterData) {
      return getFilterData(filterId);
    }
    return [];
  };

  const resetDrafts = () => {
    setSelectedFilter(null);
    setActiveFilterId(null);
    setSelectedDraftItems([]);
    setSearchText('');
    setAmountFilter({
      filterOperation: AmountFilterOperation.ALL,
      minAmount: null,
      maxAmount: null
    });
    setAmountOperation(AmountFilterOperation.ALL);
    setStartValue(null);
    setEndValue(null);
    setDateFilter({
      startDate: null,
      endDate: null
    });
  };

  const handleClose = (popoverClose: () => void) => {
    popoverClose();
    resetDrafts();
  };

  const handleFilterSelect = (filter: FilterOption) => {
    setSelectedFilter(filter);
  };

  const handleEditFilter = (filter: AppliedFilter) => {
    const filterOption = filterOptions.find(option => option.id === filter.id);
    if (!filterOption) return;

    setSelectedFilter(filterOption);
    setActiveFilterId(filter.id);

    if (filterOption.type === FilterDropdownType.DROPDOWN) {
      const values = filter.value as string[];
      setSelectedDraftItems(values);
    } else if (filterOption.type === FilterDropdownType.DATE) {
      const dateValues = filter.value as DateFilter;
      setDateFilter(dateValues);
    } else if (filterOption.type === FilterDropdownType.AMOUNT) {
      const amountValues = filter.value as AmountFilter;
      setAmountFilter(amountValues);
      setAmountOperation(amountValues.filterOperation);
      setStartValue(amountValues.minAmount);
      setEndValue(amountValues.maxAmount);
    }
  };

  const handleRemoveFilter = (filterId: string) => {
    onFilterRemove(filterId);
  };

  const handleOpenMoreFilters = () => {
    setSelectedFilter(null);
    // Pre-calculate position if there's a date filter available
    const hasDateFilter = getAvailableFilterOptions().some(option => option.type === FilterDropdownType.DATE);
    if (hasDateFilter) {
      const position = calculateDatePickerPosition(moreFiltersButtonRef.current);
      setDatePickerPosition(position);
    }
  };

  const handleApplyFilter = (selectedFilterItems: string[], filter: FilterOption) => {
    onFilterApply({ selectedFilterItems, filter });
  };

  const handleApplyAmountFilter = (amount: AmountFilter) => {
    setAmountFilter(amount);
    if (selectedFilter) {
      onFilterApply({ selectedFilterItems: amount, filter: selectedFilter });
    }
  };

  const handleApplyLocationFilter = (location: LocationFilter) => {
    onFilterApply({ selectedFilterItems: location, filter: selectedFilter });
  };

  const handleApplyDateFilter = (startDate: string | null, endDate: string | null) => {
    const dateFilterValue: DateFilter = { startDate, endDate };
    setDateFilter(dateFilterValue);
    if (selectedFilter) {
      onFilterApply({ selectedFilterItems: dateFilterValue, filter: selectedFilter });
    }
  };

  const getAvailableFilterOptions = () => {
    return filterOptions.filter(option => !appliedFilters.some(filter => filter.id === option.id));
  };

  const renderAppliedFilter = (filter: AppliedFilter) => {
    if (filter.type === FilterDropdownType.AMOUNT) {
      return (
        <CustomAmountFilter
          key={filter.id}
          amountFilter={filter.value as AmountFilter}
          setAmountFilter={handleApplyAmountFilter}
          handleDropdownOpen={() => handleEditFilter(filter)}
          handleRemoveFilter={() => handleRemoveFilter(filter.id)}
          title={filter.label}
          isAmountFilter={filter.isAmountFilter ?? true}
        />
      );
    }

    if (filter.type === FilterDropdownType.DATE) {
      return (
        <CustomDateRangePicker
          startDate={(filter.value as DateFilter).startDate}
          endDate={(filter.value as DateFilter).endDate}
          setStartDate={(date: string | null) => setDateFilter(prev => ({ ...prev, startDate: date }))}
          setEndDate={(date: string | null) => setDateFilter(prev => ({ ...prev, endDate: date }))}
          setDateRange={handleApplyDateFilter}
          placeholder={filter.label}
          filterId={`applied-date-filter-${filter.id}`}
          onClear={() => handleRemoveFilter(filter.id)}
          onOpenDatePicker={() => handleEditFilter(filter)}
          {...filter.dateFilterProps}
        />
      );
    }

    return (
      <Popover>
        {({ open, close }) => (
          <>
            <Popover.Button>
              <button
                ref={appliedFilterButtonRef}
                className={`flex items-center 
              gap-x-2 rounded-md border border-secondary bg-secondaryBtn  
              px-3 py-2 text-sbase font-semibold text-primaryText shadow 
             outline-none transition-all duration-150 ease-linear focus:outline-none
             ${buttonClassName}`}
                aria-label={`Edit ${filter.label} filter`}
                tabIndex={0}
                onClick={() => handleEditFilter(filter)}>
                <CrossIcon
                  onClick={e => {
                    e.stopPropagation();
                    handleRemoveFilter(filter.id);
                  }}
                />
                <span>{filter.label}</span>
                <span> {' | '} </span>
                <span className={'text-secondary'}>{filter.displayValue}</span>
                <DownArrowIcon className={open ? 'shrink-0 rotate-180' : 'shrink-0'} />
              </button>
            </Popover.Button>
            <Popover.Panel>{renderFilterPopover(filter, close)}</Popover.Panel>
          </>
        )}
      </Popover>
    );
  };

  const renderFilterPopover = (filter: AppliedFilter, popoverClose: () => void) => {
    if (!selectedFilter || filter.id !== activeFilterId) return null;

    return (
      <div
        className="absolute z-50 mt-2 rounded-md border border-secondaryBtn bg-white shadow-lg"
        style={{ minWidth: '208px' }}>
        {selectedFilter.type === FilterDropdownType.LOCATION && (
          <div className="pt-3">
            <LocationFilterPopover
              anchorEl={appliedFilterButtonRef.current}
              isOpen={true}
              onClose={() => handleClose(popoverClose)}
              onApply={handleApplyLocationFilter}
              initialLocation={filter.value as LocationFilter}
            />
          </div>
        )}
        {selectedFilter.type === FilterDropdownType.DROPDOWN && (
          <div className="w-52 pt-3">
            <DropdownPopoverPanel
              data={getFilterOptions(selectedFilter.id)}
              dataKey={selectedFilter.nameKey || 'name'}
              selectedDraftArray={selectedDraftItems}
              setSelectedDraftArray={setSelectedDraftItems}
              setSelectedArray={(items: string[]) => handleApplyFilter(items, selectedFilter)}
              searchText={searchText}
              setSearchText={setSearchText}
              dataIdKey={selectedFilter.idKey || 'id'}
              filterPlaceholderText={`Select ${selectedFilter.label.toLowerCase()}`}
              filterPlaceholderStyle="text-gray-500"
              searchPlaceHolderText={`Search ${selectedFilter.label.toLowerCase()}`}
              searchInputStyle="w-full p-2 border rounded"
              allowSearch={selectedFilter.showSearchInFilter ?? true}
              showResultsCount={true}
              filterId={selectedFilter.id}
              secondaryDataKey={selectedFilter.secondaryDataKey || ''}
              searchFilterId={`search-${selectedFilter.id}`}
              close={() => handleClose(popoverClose)}
              showSeparatorAfterFirstItem={selectedFilter?.hasNoOption ?? false}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Applied Filters */}
      {appliedFilters.map(filter => (
        <div key={filter.id} className="relative flex shrink-0 items-center">
          {renderAppliedFilter(filter)}
        </div>
      ))}

      {/* More Filters Button */}
      <If condition={appliedFilters.length !== filterOptions.length}>
        <Popover className="relative">
          {({ open, close }) => (
            <>
              <Popover.Button
                ref={moreFiltersButtonRef}
                className={`flex shrink-0 items-center 
                gap-x-2 rounded-md border border-dashed border-border bg-primary  
                px-3 py-2 text-sbase font-semibold text-primaryText 
               outline-none transition-all duration-150 ease-linear focus:outline-none
               ${buttonClassName}`}
                onClick={handleOpenMoreFilters}
                aria-label="More filters"
                tabIndex={0}>
                <PlusIcon className="shrink-0" />
                <span>More filters</span>
                <DownArrowIcon className={open ? 'shrink-0 rotate-180' : 'shrink-0'} />
              </Popover.Button>

              {open && (
                <Popover.Panel
                  className={`absolute z-50 ${
                    selectedFilter?.type === FilterDropdownType.DATE && datePickerPosition === 'right'
                      ? 'right-0 transition-all ease-linear'
                      : ''
                  } mt-2 overflow-hidden rounded-md border border-border bg-white`}
                  style={{ minWidth: '208px' }}>
                  {!selectedFilter ? (
                    <div className="flex flex-col px-3 py-1.5">
                      {getAvailableFilterOptions().map(option => (
                        <div
                          key={option.id}
                          className="flex cursor-pointer items-center gap-x-2.5 rounded py-1.5 hover:bg-gray-50"
                          onClick={() => handleFilterSelect(option)}
                          onKeyDown={e => e.key === 'Enter' && handleFilterSelect(option)}
                          tabIndex={0}
                          aria-label={`Select ${option.label} filter`}>
                          <PlusIcon className="h-4 w-4 shrink-0" />
                          <span className="text-sbase font-semibold text-primaryText">{option.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {selectedFilter.type === FilterDropdownType.AMOUNT && (
                        <div className="pt-3">
                          <AmountFilterPopoverPanel
                            operation={amountOperation}
                            setOperation={setAmountOperation}
                            startValue={startValue}
                            setStartValue={setStartValue}
                            endValue={endValue}
                            setEndValue={setEndValue}
                            amountFilter={amountFilter}
                            setAmountFilter={handleApplyAmountFilter}
                            close={() => handleClose(close)}
                            title={selectedFilter.label}
                            isAmountFilter={selectedFilter.isAmountFilter ?? true}
                          />
                        </div>
                      )}
                      {selectedFilter.type === FilterDropdownType.LOCATION && (
                        <div className="pt-3">
                          <LocationFilterPopover
                            anchorEl={moreFiltersButtonRef.current}
                            isOpen={true}
                            onClose={() => handleClose(close)}
                            onApply={handleApplyLocationFilter}
                            initialLocation={{ zipCode: '', rangeInMiles: 0 }}
                          />
                        </div>
                      )}
                      {selectedFilter.type === FilterDropdownType.DATE && (
                        <CustomDateRangePicker
                          startDate={dateFilter.startDate}
                          endDate={dateFilter.endDate}
                          setStartDate={(date: string | null) => setDateFilter(prev => ({ ...prev, startDate: date }))}
                          setEndDate={(date: string | null) => setDateFilter(prev => ({ ...prev, endDate: date }))}
                          setDateRange={handleApplyDateFilter}
                          placeholder={`Select ${selectedFilter.label.toLowerCase()}`}
                          filterId={`date-filter-${selectedFilter.id}`}
                          renderContentOnly={true}
                          onContentClose={() => handleClose(close)}
                          {...selectedFilter.dateFilterProps}
                        />
                      )}
                      {selectedFilter.type === FilterDropdownType.DROPDOWN && (
                        <div className="w-52 pt-3">
                          <DropdownPopoverPanel
                            data={getFilterOptions(selectedFilter.id)}
                            dataKey={selectedFilter.nameKey || 'name'}
                            selectedDraftArray={selectedDraftItems}
                            setSelectedDraftArray={setSelectedDraftItems}
                            setSelectedArray={(items: string[]) => handleApplyFilter(items, selectedFilter)}
                            searchText={searchText}
                            setSearchText={setSearchText}
                            dataIdKey={selectedFilter.idKey || 'id'}
                            filterPlaceholderText={`Select ${selectedFilter.label.toLowerCase()}`}
                            filterPlaceholderStyle="text-gray-500"
                            searchPlaceHolderText={`Search ${selectedFilter.label.toLowerCase()}`}
                            searchInputStyle="w-full p-2 border rounded"
                            allowSearch={selectedFilter.showSearchInFilter ?? true}
                            showResultsCount={true}
                            filterId={selectedFilter.id}
                            secondaryDataKey={selectedFilter.secondaryDataKey || ''}
                            searchFilterId={`search-${selectedFilter.id}`}
                            close={() => handleClose(close)}
                            showSeparatorAfterFirstItem={selectedFilter?.hasNoOption ?? false}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Popover.Panel>
              )}
            </>
          )}
        </Popover>
      </If>
    </>
  );
};

export default MoreFilters;
